import type {
  Contribution,
  CreateContributionPayload,
  CreateGoalPayload,
  Goal,
  GoalBalanceSource,
  GoalPacing,
  GoalStatus,
  GoalsOverview,
  GoalsSummary,
  GoalType,
  UpdateGoalPayload,
} from "@fomofi/shared";

import apiClient from "./client";

/* ─────────────────────── API response types (snake_case) ───────────────────────
 * Mirrors backend/apps/goals/serializers.py + services/summary.py. Monetary
 * values arrive as Decimal strings; the adapter parses them to numbers. */

export interface ApiGoalPacing {
  progress_pct: number;
  remaining_amount: string;
  months_remaining: number | null;
  required_monthly: string;
  actual_monthly: string | null;
  projected_date: string | null;
  status: GoalStatus;
  /** Optional on the wire: a frontend deployed ahead of the backend reads
   *  `undefined` and falls back to the no-contributions copy, which is the
   *  pre-existing behaviour rather than a new wrong state. */
  contribution_window_days?: number | null;
}

export interface ApiGoal {
  id: number;
  name: string;
  goal_type: GoalType;
  target_amount: string;
  current_amount: string;
  target_date: string | null;
  icon: string;
  color: string;
  is_active: boolean;
  created_at: string;
  updated_at: string;
  pacing: ApiGoalPacing;
  forecast_allocation_key: string | null;
  account_id: number | null;
  last_four: string;
  balance_source: GoalBalanceSource;
  /** FP-021: the stored saved figure, what the user's own entries and contributions
   *  add up to. On a LINKED goal `current_amount` is the account's balance; this is
   *  what an unlink keeps (Paul, 2026-10-06: never the balance, $0 included).
   *  Optional for a backend that predates it. */
  saved_amount?: string;
  /** FP-022: whether deleting this goal also removes its Forecast bucket (this goal
   *  created it, nothing else links it, and it holds 0%), from the same server
   *  function the delete runs, so the confirm can say so beforehand. Optional for a
   *  backend that predates it; absent means "stays". */
  bucket_will_be_removed?: boolean;
  /** The linked Forecast bucket's name, for that sentence; null without a bucket. */
  forecast_bucket_name?: string | null;
  /** How many contributions are logged against this goal, and what they add up
   *  to (`goals/services/summary.py` adds both to the overview rows).
   *
   *  Optional because only the OVERVIEW carries them: the create and update
   *  responses come from the model serializer and stop at the stored fields.
   *
   *  Read these, never `current_amount`, when naming what a delete destroys —
   *  `current_amount` is user-entered and seeded at creation, so on the demo
   *  data "Lexus Rx350" has $10,000 saved and zero contributions (BEN-141 N7,
   *  #1046; mobile has read them since item 5). */
  contribution_count?: number;
  /** Decimal string, e.g. "450.00". Pairs with `contribution_count`. */
  contribution_total?: string;
}

export interface ApiGoalsSummary {
  active_count: number;
  total_saved: string;
  total_target: string;
  overall_percent: number;
  total_required_monthly: string;
  achieved_count: number;
  behind_count: number;
}

export interface ApiGoalsOverview {
  goals: ApiGoal[];
  summary: ApiGoalsSummary;
}

export interface ApiContribution {
  id: number;
  amount: string;
  date: string;
  note: string;
  created_at: string;
}

/* ───────────────────────────────── requests ───────────────────────────────── */

export async function getGoalsOverview(): Promise<ApiGoalsOverview> {
  const { data } = await apiClient.get<ApiGoalsOverview>("/goals/");
  return data;
}

/** Map the UI's create/update payload onto the backend's write serializer. */
function toGoalBody(p: CreateGoalPayload | UpdateGoalPayload): Record<string, unknown> {
  const body: Record<string, unknown> = {};
  if (p.name !== undefined) body.name = p.name;
  if (p.type !== undefined) body.goal_type = p.type;
  if (p.targetAmount !== undefined) body.target_amount = p.targetAmount;
  if (p.currentAmount !== undefined) body.current_amount = p.currentAmount;
  if (p.targetDate !== undefined) body.target_date = p.targetDate;
  if (p.icon !== undefined) body.icon = p.icon;
  if (p.color !== undefined) body.color = p.color;
  if (p.forecastAllocationKey !== undefined) {
    body.forecast_allocation_key = p.forecastAllocationKey;
  }
  if (p.createForecastBucket !== undefined) body.create_forecast_bucket = p.createForecastBucket;
  // `account`, not `account_id`: the write serializer takes the relation by
  // primary key and the read shape reports it as `account_id`.
  if (p.account !== undefined) body.account = p.account;
  return body;
}

export async function createGoal(payload: CreateGoalPayload): Promise<ApiGoal> {
  const { data } = await apiClient.post<ApiGoal>("/goals/list/", toGoalBody(payload));
  return data;
}

export async function updateGoal(id: number, payload: UpdateGoalPayload): Promise<ApiGoal> {
  const { data } = await apiClient.patch<ApiGoal>(`/goals/${id}/`, toGoalBody(payload));
  return data;
}

export async function deleteGoal(id: number): Promise<void> {
  await apiClient.delete(`/goals/${id}/`);
}

export async function addContribution(
  goalId: number,
  payload: CreateContributionPayload,
): Promise<ApiContribution> {
  const body: Record<string, unknown> = { amount: payload.amount, date: payload.date };
  if (payload.note !== undefined) body.note = payload.note;
  const { data } = await apiClient.post<ApiContribution>(`/goals/${goalId}/contributions/`, body);
  return data;
}

export async function getContributions(goalId: number): Promise<ApiContribution[]> {
  const { data } = await apiClient.get<ApiContribution[]>(`/goals/${goalId}/contributions/`);
  return data;
}

export async function removeContribution(goalId: number, contributionId: number): Promise<void> {
  await apiClient.delete(`/goals/${goalId}/contributions/${contributionId}/`);
}

/* ───────────────────────────────── adapter ───────────────────────────────── */

const num = (s: string | null | undefined): number => (s == null ? 0 : parseFloat(s));

function adaptPacing(p: ApiGoalPacing): GoalPacing {
  return {
    progressPct: p.progress_pct,
    remainingAmount: num(p.remaining_amount),
    monthsRemaining: p.months_remaining,
    requiredMonthly: num(p.required_monthly),
    actualMonthly: p.actual_monthly == null ? null : num(p.actual_monthly),
    contributionWindowDays: p.contribution_window_days ?? null,
    projectedDate: p.projected_date,
    status: p.status,
  };
}

export function adaptGoal(g: ApiGoal): Goal {
  return {
    id: g.id,
    name: g.name,
    type: g.goal_type,
    targetAmount: num(g.target_amount),
    currentAmount: num(g.current_amount),
    targetDate: g.target_date,
    icon: g.icon,
    color: g.color,
    pacing: adaptPacing(g.pacing),
    forecastAllocationKey: g.forecast_allocation_key ?? null,
    accountId: g.account_id ?? null,
    lastFour: g.last_four ?? "",
    // Defaulted rather than required on the wire: a client deployed ahead of the
    // backend reads undefined and renders the manual form it rendered before.
    balanceSource: g.balance_source ?? (g.account_id ? "linked" : "manual"),
    // 0 when absent, which is the honest reading on a write response and on an
    // older backend: it makes the delete dialog say only "this can't be undone"
    // rather than name a ledger it cannot see.
    contributionCount: g.contribution_count ?? 0,
    contributionTotal: num(g.contribution_total),
    // FP-022: the delete confirm's bucket line. Absent on write responses and older
    // backends; `goalDeleteBucketLine` reads absent as "stays".
    forecastBucketName: g.forecast_bucket_name ?? null,
    bucketWillBeRemoved: g.bucket_will_be_removed,
  };
}

function adaptSummary(s: ApiGoalsSummary): GoalsSummary {
  return {
    activeCount: s.active_count,
    totalSaved: num(s.total_saved),
    totalTarget: num(s.total_target),
    overallPercent: s.overall_percent,
    totalRequiredMonthly: num(s.total_required_monthly),
    achievedCount: s.achieved_count,
    behindCount: s.behind_count,
  };
}

export function toGoalsOverview(api: ApiGoalsOverview): GoalsOverview {
  return {
    goals: api.goals.map(adaptGoal),
    summary: adaptSummary(api.summary),
  };
}

export function adaptContribution(c: ApiContribution): Contribution {
  return { id: c.id, amount: num(c.amount), date: c.date, note: c.note };
}
